import { inject, Injectable } from '@angular/core'
import { TranslateService } from '@ngx-translate/core'
import { BaseStorageService } from './base-storage.service'
import translationEN from '../../../public/i18n/en.json'
import translationCN from '../../../public/i18n/zh-CN.json'
import translationHK from '../../../public/i18n/zh-HK.json'
import { DatePipe, registerLocaleData } from '@angular/common'
import localeZhHans from '@angular/common/locales/zh-Hans'
import localeZhHant from '@angular/common/locales/zh-Hant'
import { BehaviorSubject } from 'rxjs'

export enum Locale {
  EN = 'en',
  ZH_CN = 'zh-CN',
  ZH_HK = 'zh-HK',
  DEFAULT = Locale.EN
}

export enum DateFormatter {
  DATE_EN = 'd MMMM y',
  WEEKDAY = 'EEEE'
}

const LOCALE_KEY = 'lang'

@Injectable({ providedIn: 'root' })
export class LanguageService {
  private transService = inject(TranslateService)
  private storageService = inject(BaseStorageService)
  private datePipe = new DatePipe(Locale.DEFAULT)
  private langChangeSubject = new BehaviorSubject(Locale.DEFAULT)
  private langSource$ = this.langChangeSubject.asObservable()

  get langSource() {
    return this.langSource$
  }

  preInit() {
    registerLocaleData(localeZhHans, Locale.ZH_CN)
    registerLocaleData(localeZhHant, Locale.ZH_HK)
    this.transService.setTranslation(Locale.EN, translationEN)
    this.transService.setTranslation(Locale.ZH_CN, translationCN)
    this.transService.setTranslation(Locale.ZH_HK, translationHK)
    this.transService.setDefaultLang(Locale.DEFAULT)
  }

  restore() {
    let locale = this.storageService.get(LOCALE_KEY)

    if (!locale || locale === this.getCurrentLocale()) {
      return
    }

    this.setLangWithLocale(<Locale>locale)
  }

  setLangWithLocale(locale: Locale) {
    this.transService.use(locale)
    this.langChangeSubject.next(locale)
    this.storageService.set(LOCALE_KEY, locale)
    Reflect.set(this.datePipe, 'locale', <string>locale)
  }

  getCurrentLocale() {
    return <Locale>this.transService.currentLang
  }

  dateTransform(date: Date | string | number, format?: string) {
    let locale = this.getCurrentLocale()

    if (format) {
      return this.datePipe.transform(date, format)
    }

    return this.datePipe.transform(
      date,
      locale === Locale.EN ? DateFormatter.DATE_EN : undefined
    )
  }

  weekdayTransform(date: Date | string | number, format?: string) {
    return this.datePipe.transform(
      date,
      format ? format : DateFormatter.WEEKDAY
    )
  }
}
